import { useEffect, useRef, type ReactNode } from 'react'

export interface ModalProps {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
}

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

export function Modal({ open, title, onClose, children }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const lastFocusedRef = useRef<Element | null>(null)

  useEffect(() => {
    if (!open) return
    lastFocusedRef.current = document.activeElement
    // Focus the first focusable element inside the modal, falling back to the
    // modal container itself.
    const content = contentRef.current
    if (!content) return
    const focusable = content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
    const target = focusable[0] ?? content
    target.focus()

    const body = document.body
    const originalOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    return () => {
      body.style.overflow = originalOverflow
      if (lastFocusedRef.current instanceof HTMLElement) {
        lastFocusedRef.current.focus()
      }
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const content = contentRef.current
      if (!content) return
      const focusable = Array.from(content.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)).filter(
        (el) => el.offsetParent !== null,
      )
      if (focusable.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (e.shiftKey && active === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null
  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 overflow-y-auto bg-ostrel-navy/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="flex min-h-full items-center justify-center">
        <div
          ref={contentRef}
          tabIndex={-1}
          className="flex max-h-[90vh] w-full max-w-md flex-col rounded-sm border border-ostrel-line bg-ostrel-card shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex shrink-0 items-center justify-between border-b border-ostrel-line px-4 py-3">
            <h2 className="font-display text-lg text-ostrel-ink">{title}</h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="-m-1 p-1.5 text-ostrel-ink-muted transition-colors hover:text-ostrel-ink focus:outline-none focus:ring-2 focus:ring-ostrel-gold"
            >
              ✕
            </button>
          </header>
          <div className="overflow-y-auto p-4 text-ostrel-ink">{children}</div>
        </div>
      </div>
    </div>
  )
}
